/**
 * Pinned summary header for the business (organization) page (requirements p.4: "pinned summary header stays visible
 * while scrolling"). Shows the identity an agent needs at a glance: name, type, status, prospect/disabled flags and
 * the stable identifiers. Slice 2 adds `badges` (vendor kind, program affiliation, sales owner, Concierge) and
 * `pinnedBadges` (the compact subset shown in the pinned bar).
 *
 * Why not CSS `position: sticky`: the dashboard <main> has overflow-x:auto, which makes it the sticky containing
 * scroller while the WINDOW is what scrolls, so a sticky header silently scrolls away (caught by the QA render
 * check). Instead, once the full header leaves the viewport a compact copy is shown `position: fixed`, aligned to the
 * content column.
 */

import React, { useEffect, useRef, useState } from "react";
import type { Organization } from "../../types/organization.types";
import { organizationTypeLabel } from "../../types/organization.types";

interface OrganizationSummaryHeaderProps {
  organization: Organization;
  actions?: React.ReactNode;
  onBack?: () => void;
  badges?: React.ReactNode;
  pinnedBadges?: React.ReactNode;
}

const statusStyle = (status?: string | null, archived?: boolean): string => {
  if (archived) {
    return "bg-gray-200 text-gray-700";
  }
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";
    case "inactive":
      return "bg-red-100 text-red-800";
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const statusLabel = (status?: string | null, archived?: boolean): string => {
  if (archived) {
    return "Disabled";
  }
  if (!status) {
    return "Unknown";
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const Id: React.FC<{
  label: string;
  value?: string | null;
  testId: string;
}> = ({ label, value, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <dt className="text-[11px] uppercase tracking-wide text-gray-500">
      {label}
    </dt>
    <dd className="truncate font-mono text-sm text-gray-900">
      {value && String(value).trim() ? (
        value
      ) : (
        <span className="text-gray-400">—</span>
      )}
    </dd>
  </div>
);

const OrganizationSummaryHeader: React.FC<OrganizationSummaryHeaderProps> = ({
  organization,
  actions,
  onBack,
  badges,
  pinnedBadges,
}) => {
  const typeLabel = organizationTypeLabel(organization.organization_type);
  const fullRef = useRef<HTMLDivElement | null>(null);
  const [pinned, setPinned] = useState<{ left: number; width: number } | null>(
    null,
  );

  useEffect(() => {
    const el = fullRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const r = el.getBoundingClientRect();
      setPinned(
        r.height > 0 && r.bottom <= 0 ? { left: r.left, width: r.width } : null,
      );
    };
    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(() => measure(), {
        threshold: [0, 1],
      });
      observer.observe(el);
    }
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    measure();
    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, []);

  const statusChip = (
    <span
      className={`rounded px-2 py-0.5 font-medium ${statusStyle(organization.status, organization.is_archived)}`}
    >
      {statusLabel(organization.status, organization.is_archived)}
    </span>
  );

  return (
    <>
      {pinned && (
        <div
          className="fixed top-0 z-30 border-b border-gray-200 bg-white/95 px-4 py-2 shadow-sm backdrop-blur"
          style={{ left: pinned.left, width: pinned.width }}
          data-testid="organization-summary-pinned"
          role="region"
          aria-label="Organization summary"
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="truncate text-base font-semibold text-gray-900">
              {organization.name}
            </span>
            {typeLabel && (
              <span className="rounded bg-blue-50 px-2 py-0.5 text-blue-800">
                {typeLabel}
              </span>
            )}
            {statusChip}
            {organization.is_prospect && (
              <span className="rounded bg-yellow-100 px-2 py-0.5 text-yellow-800">
                Prospect
              </span>
            )}
            {pinnedBadges}
            <span className="font-mono text-gray-700">
              Org {organization.organization_id || "—"}
            </span>
            <span className="font-mono text-gray-700">
              FID {organization.facility_id || "—"}
            </span>
            <span className="font-mono text-gray-700">
              Aff {organization.affiliate_id || "—"}
            </span>
            <span className="font-mono text-gray-700">
              Acct {organization.account_number || "—"}
            </span>
            <span className="font-mono text-gray-700">
              Terr {organization.territory_designation || "—"}
            </span>
          </div>
        </div>
      )}
      <div
        ref={fullRef}
        className="-mx-4 border-b border-gray-200 bg-white px-4 py-3 sm:-mx-6 sm:px-6"
        data-testid="organization-summary-header"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="mt-1 text-gray-600 hover:text-gray-900"
                aria-label="Back to organizations"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                  />
                </svg>
              </button>
            )}
            <div className="min-w-0">
              <h1
                className="truncate text-2xl font-bold text-gray-900"
                title={organization.name}
              >
                {organization.name}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                {typeLabel && (
                  <span
                    className="rounded bg-blue-50 px-2 py-0.5 text-blue-800"
                    data-testid="summary-type"
                  >
                    {typeLabel}
                  </span>
                )}
                <span
                  className={`rounded px-2 py-0.5 font-medium ${statusStyle(organization.status, organization.is_archived)}`}
                  data-testid="summary-status"
                >
                  {statusLabel(organization.status, organization.is_archived)}
                </span>
                {organization.is_prospect && (
                  <span className="rounded bg-yellow-100 px-2 py-0.5 text-yellow-800">
                    Prospect
                  </span>
                )}
                {badges}
                {organization.legal_name &&
                  organization.legal_name !== organization.name && (
                    <span className="text-gray-500">
                      Legal name: {organization.legal_name}
                    </span>
                  )}
              </div>
            </div>
          </div>
          {actions && (
            <div className="flex flex-wrap items-center gap-2">{actions}</div>
          )}
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-5">
          <Id
            label="Organization ID"
            value={organization.organization_id}
            testId="summary-org-id"
          />
          <Id
            label="Facility ID"
            value={organization.facility_id}
            testId="summary-facility-id"
          />
          <Id
            label="Affiliate ID"
            value={organization.affiliate_id}
            testId="summary-affiliate-id"
          />
          <Id
            label="Account Number"
            value={organization.account_number}
            testId="summary-account-number"
          />
          <Id
            label="Territory"
            value={organization.territory_designation}
            testId="summary-territory"
          />
        </dl>
      </div>
    </>
  );
};

export default OrganizationSummaryHeader;
